﻿import { autorun } from 'mobx';
import { CoordinateNotation } from 'msg-ui-react/dist/interfaces/CoordinateNotation';
import CoordinateUtilities from 'msg-ui-react/dist/utils/CoordinateUtilities';
import { createEmpty } from 'ol/extent';
import Graticule, { Options as GraticuleLayerOptions } from 'ol/layer/Graticule';
import RenderEvent from 'ol/render/Event';
import { GraticuleState } from '../models/GraticuleState';
import { MobxReactionDisposer } from '../models/MobxReactionDisposer';
import { UnitsState } from '../models/UnitsState';
import { latGraticuleLabel, lonGraticuleLabel } from "../styles/Graticule";


export type { GraticuleLayerOptions };

export interface GraticuleLayerStates {
    graticule: GraticuleState,
    units: UnitsState
}

class GraticuleLayer extends Graticule {

    protected mobxDisposer = new MobxReactionDisposer();

    private notation_: CoordinateNotation = "Deg";
    private lastRenderEvent: RenderEvent | undefined;

    constructor(
        states: GraticuleLayerStates,
        options?: GraticuleLayerOptions) {
        super({
            className: 'ol-layer ol-layer-no-pointer-events',
            showLabels: true,
            latLabelPosition: 0,
            lonLabelPosition: 1,
            latLabelFormatter: d => this.formatLat(d),
            lonLabelFormatter: d => this.formatLon(d),
            latLabelStyle: latGraticuleLabel(),
            lonLabelStyle: lonGraticuleLabel(),
            zIndex: Infinity,
            ...options
        });

        this.on("postrender", this.setLastRenderEvent);

        this.mobxDisposer.add([
            autorun(() => {
                this.setVisible(states.graticule.visible);
            }),

            autorun(() => {
                this.notation_ = states.units.coordinateNotation;
                this.refresh();
            })
        ]);
    }    

    private formatLat = (degrees: number) => CoordinateUtilities.getFormattedLat(degrees, this.notation_, 0);
    private formatLon = (degrees: number) => CoordinateUtilities.getFormattedLon(degrees, this.notation_, 0, true)

    private setLastRenderEvent = (evt: RenderEvent) => this.lastRenderEvent = evt;

    private refresh() {
        try {
            if (this.lastRenderEvent?.frameState) {
                const { extent, viewState } = this.lastRenderEvent.frameState;
                if (extent == null)
                    throw new Error("extent cannot be null");

                const source = this.getSource();
                if (source) {
                    source.loadFeatures(createEmpty(), viewState.resolution, viewState.projection);
                    source.loadFeatures(extent, viewState.resolution, viewState.projection);
                }
            }
        }
        catch (e) {
            console.log(e)
        }
    }

    public dispose() {
        this.un("postrender", this.setLastRenderEvent);
        this.mobxDisposer.clear();
        super.dispose();
    }
}

export default GraticuleLayer;